import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  resultCount?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  resultCount,
}) => {
  const safeTotalPages = Math.max(1, totalPages || 1);

  const renderPageButtons = () => {
    if (safeTotalPages === 1) {
      return (
        <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
          1
        </button>
      );
    }

    if (safeTotalPages === 2) {
      return (
        <>
          <button
            className={`px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm ${
              currentPage === 1
                ? "bg-[#DAF9F9] text-[#003E3E]"
                : "hover:bg-[#DAF9F9]"
            }`}
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          <button
            className={`px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm ${
              currentPage === 2
                ? "bg-[#DAF9F9] text-[#003E3E]"
                : "hover:bg-[#DAF9F9]"
            }`}
            onClick={() => onPageChange(2)}
          >
            2
          </button>
        </>
      );
    }

    if (safeTotalPages === 3) {
      return (
        <>
          {[1, 2, 3].map((page) => (
            <button
              key={page}
              className={`px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm ${
                currentPage === page
                  ? "bg-[#DAF9F9] text-[#003E3E]"
                  : "hover:bg-[#DAF9F9]"
              }`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ))}
        </>
      );
    }

    if (currentPage === 1) {
      return (
        <>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
            1
          </button>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(2)}
          >
            2
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
            ...
          </button>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(safeTotalPages)}
          >
            {safeTotalPages}
          </button>
        </>
      );
    }

    if (currentPage === 2) {
      return (
        <>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
            2
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
            ...
          </button>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(safeTotalPages)}
          >
            {safeTotalPages}
          </button>
        </>
      );
    }

    if (currentPage === safeTotalPages) {
      return (
        <>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
            ...
          </button>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(safeTotalPages - 1)}
          >
            {safeTotalPages - 1}
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
            {safeTotalPages}
          </button>
        </>
      );
    }

    if (currentPage === safeTotalPages - 1) {
      return (
        <>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
            ...
          </button>
          <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
            {safeTotalPages - 1}
          </button>
          <button
            className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
            onClick={() => onPageChange(safeTotalPages)}
          >
            {safeTotalPages}
          </button>
        </>
      );
    }

    return (
      <>
        <button
          className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
          onClick={() => onPageChange(1)}
        >
          1
        </button>
        <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
          ...
        </button>
        <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] bg-[#DAF9F9] text-[#003E3E] rounded text-sm">
          {currentPage}
        </button>
        <button className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded text-sm cursor-default">
          ...
        </button>
        <button
          className="px-2 sm:px-3 py-1 border-[0.5px] border-[#0DB5B5] rounded hover:bg-[#DAF9F9] transition-colors text-sm"
          onClick={() => onPageChange(safeTotalPages)}
        >
          {safeTotalPages}
        </button>
      </>
    );
  };

  return (
    <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 flex-shrink-0">
      {resultCount !== undefined && (
        <div className="text-xs sm:text-sm text-gray-500">
          Result {resultCount}
        </div>
      )}
      <div className="flex gap-2">
        <button
          className="px-2 sm:px-3 py-1 border-[0.5px] border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>

        {renderPageButtons()}

        <button
          className="px-2 sm:px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => onPageChange(Math.min(safeTotalPages, currentPage + 1))}
          disabled={currentPage === safeTotalPages}
        >
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;